import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {Sort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {HttpClient} from "@angular/common/http";
import {of, Subject} from "rxjs";
import {catchError, finalize, takeUntil} from "rxjs/operators";
import {
  IReferralCampaignHistoryDailyTotal,
  IReferralCampaignHistoryMetadata,
  IReferralTransactionRow,
  IReferralTransactionsPage,
} from "../../services/interfaces/referral-campaigns.interface";
import {environment} from "../../../../../../environments/environment";
@Component({
  selector: 'app-ref-income-log',
  templateUrl: './ref-income-log.component.html',
  styleUrls: ['./ref-income-log.component.scss']
})
export class RefIncomeLogComponent implements OnInit, OnDestroy {
  @ViewChild('txPaginator', {static: false}) txPaginator!: MatPaginator;

  public isGoFast: boolean = environment.projectName === 'CSGOFAST';

  // История по дням (FAST_NEW_BACK-2269): подытоги по всем кампаниям рефовода (dailyTotals).
  public dailyHistoryColumns = [
    'date',
    'referralsTotalCount',
    'referralsActivatedCount',
    'referralsLostCount',
    'rewardCoins',
    'forecastRewardCoins',
  ];
  public dailyHistoryDataSource: MatTableDataSource<IReferralCampaignHistoryDailyTotal> = new MatTableDataSource();

  public refTxV2Columns = [
    'referralUserId',
    'code',
    'transactionId',
    'cycleStatus',
    'balanceStart',
    'balanceEnd',
    'sumDeposits',
    'sumWithdraw',
    'rewardCoinsCalculated',
    'receivedCoins',
    'isHold',
    'calculatedAt',
    'creditedAt',
    'lastWalletEventAt',
    'date',
  ];
  public refTxV2DataSource: MatTableDataSource<IReferralTransactionRow> = new MatTableDataSource();

  public isLoadingDaily = true;
  public isLoadingV2 = true;
  public totalRowsV2 = 0;
  public pageSize = 10;
  public currentPage = 0;
  public pageSizeOptions: number[] = [10, 25, 50, 100];
  // Серверная сортировка транзакций (по умолчанию — от новых к старым по дате создания).
  private txSortActive = 'createdAt';
  private txSortDirection: 'asc' | 'desc' = 'desc';
  private userId!: number;
  private readonly destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    this.userId = id != null ? Number(id) : NaN;
    this.loadDailyHistory();
    this.loadTransactions();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  pageChanged(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.loadTransactions();
  }

  /**
   * Серверная сортировка транзакций по клику на стрелки заголовка «Дата».
   * Колонка `date` маппится на поле сущности `createdAt`. Сбрасываем на первую
   * страницу и перезапрашиваем с сервера (сортируется вся выборка, не текущая страница).
   */
  onTxSort(sort: Sort): void {
    this.txSortActive = sort.active === 'date' ? 'createdAt' : sort.active;
    this.txSortDirection = sort.direction === 'asc' ? 'asc' : 'desc';
    this.currentPage = 0;
    this.loadTransactions();
  }

  /** ID приглашённого для ссылки и отображения (как в легаси, но показываем id). */
  invitedUserId(row: IReferralTransactionRow): number {
    const fromReferral = row.referral?.userId;
    if (fromReferral != null && Number.isFinite(fromReferral)) {
      return fromReferral;
    }
    return row.referralUserId;
  }

  /** ID первого финансового wallet-события, открывшего период. */
  transactionId(row: IReferralTransactionRow): string {
    const value = row.metaData?.['transactionId'];
    return value == null ? '—' : String(value);
  }

  /** Человекочитаемая стадия периода по датам расчёта и месячного переноса. */
  cycleStatus(row: IReferralTransactionRow): string {
    if (this.isAdminReset(row)) {
      return 'Сброшен';
    }
    if (this.isBindingSwitchCanceled(row)) {
      return 'Отменён';
    }
    if (row.creditedAt) {
      return 'Перенесён';
    }
    if (row.calculatedAt) {
      return 'Рассчитан';
    }
    return 'Активен';
  }

  /** Поясняет текущую стадию расчёта и доступность результата рефоводу. */
  cycleStatusTooltip(row: IReferralTransactionRow): string {
    if (this.isAdminReset(row)) {
      return 'Период закрыт административным сбросом без переноса результата в доход.';
    }
    if (this.isBindingSwitchCanceled(row)) {
      return 'Период закрыт при смене реферальной привязки без начисления результата.';
    }
    if (row.creditedAt) {
      return 'Результат периода уже перенесён в доход рефовода и его кампании.';
    }
    if (row.calculatedAt) {
      return 'Период рассчитан при балансе не больше 1 F, но результат ещё не перенесён в доход месячной обработкой.';
    }
    return 'Период ожидает расчёта при балансе реферала не больше 1 F.';
  }

  private isAdminReset(row: IReferralTransactionRow): boolean {
    return row.metaData?.['adminResetAt'] != null;
  }

  private isBindingSwitchCanceled(row: IReferralTransactionRow): boolean {
    return row.metaData?.['cancelReason'] === 'bindingSwitch';
  }

  centsValue(n: number | null | undefined): number {
    if (n == null || !Number.isFinite(n)) {
      return 0;
    }
    return n;
  }

  /**
   * История по дням рефовода (dailyTotals по всем его кампаниям). `dailyTotals` не зависит от пагинации
   * (агрегат по всему диапазону), поэтому запрашиваем минимальный `limit` — нужны только подытоги по дням.
   */
  private loadDailyHistory(): void {
    if (!Number.isFinite(this.userId)) {
      this.isLoadingDaily = false;
      return;
    }

    this.isLoadingDaily = true;
    this.http.post<IReferralCampaignHistoryMetadata>(
      `${environment.GATEWAY_URL}/referral/campaign-history/get`,
      {
        referrerUserId: this.userId,
        pagination: {limit: 1, offset: 0},
      },
      {withCredentials: true},
    ).pipe(
      takeUntil(this.destroyed$),
      catchError(() => of(null)),
      finalize(() => {
        this.isLoadingDaily = false;
      }),
    ).subscribe((res) => {
      this.dailyHistoryDataSource.data = res?.dailyTotals ?? [];
    });
  }

  private loadTransactions(): void {
    if (!Number.isFinite(this.userId)) {
      this.isLoadingV2 = false;
      return;
    }

    const limit = this.pageSize;
    const offset = this.currentPage * limit;

    this.isLoadingV2 = true;
    this.http.post<IReferralTransactionsPage>(
      `${environment.GATEWAY_URL}/referral/${this.userId}/referral-transactions`,
      {
        pagination: {limit, offset},
        referralsVisible: true,
        referralCampaignsVisible: true,
        // Серверная сортировка (по умолчанию createdAt desc — от новых к старым).
        sort: {active: this.txSortActive, direction: this.txSortDirection},
      },
      {withCredentials: true},
    ).pipe(
      takeUntil(this.destroyed$),
      catchError(() => of(null)),
      finalize(() => {
        this.isLoadingV2 = false;
      }),
    ).subscribe((res) => {
      if (res) {
        this.refTxV2DataSource.data = res.transactions ?? [];
        this.totalRowsV2 = Number(res.metadata?.amount ?? 0);
        if (this.txPaginator) {
          this.txPaginator.pageIndex = this.currentPage;
        }
      } else {
        this.refTxV2DataSource.data = [];
        this.totalRowsV2 = 0;
      }
    });
  }
}
